import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAdminBoard,
  deleteAdminBoard,
  getAdminBoardById,
  getAdminBoards,
  removeAdminBoardImage,
  updateAdminBoard,
  uploadAdminBoardImage,
} from "../../../api/boardsAdmin";
import AdminProductPdfsPanel from "./AdminProductPdfsPanel";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";

const LANGS = ["en", "ar", "ku"];

const emptyLangFields = () => ({
  title: "",
  heroDescription: "",
  recommendationsIntro: "",
});

const EMPTY_FORM = {
  boardType: "",
  language: "en",
  imageUrl: "",
  isActive: true,
  translations: {
    en: emptyLangFields(),
    ar: emptyLangFields(),
    ku: emptyLangFields(),
  },
  keyFeatures: [""],
  specificationSections: [
    { heading: "", displayOrder: 1, items: [{ label: "", value: "", displayOrder: 1 }] },
  ],
  recommendationApplications: [{ numberLabel: "01", text: "" }],
  recommendationAccordionItems: [{ title: "", displayOrder: 1, bullets: [{ text: "", displayOrder: 1 }] }],
  sustainabilityCards: [{ title: "", text: "", iconUrl: "" }],
  sustainabilityStorageItems: [{ text: "" }],
};

const getBoardName = (board) =>
  board?.title || board?.name || board?.heroTitle || board?.slug || board?.id || "Board";

const mapBoardToForm = (board) => ({
  boardType: board?.boardType || board?.type || "",
  language: String(board?.language || "en").toLowerCase(),
  imageUrl: board?.imageUrl || "",
  translations: (() => {
    const base = {
      en: {
        title: board?.title || board?.name || "",
        heroDescription: board?.heroDescription || board?.description || "",
        recommendationsIntro: board?.recommendationsIntro || "",
      },
      ar: emptyLangFields(),
      ku: emptyLangFields(),
    };
    if (!Array.isArray(board?.translations)) return base;
    const next = { ...base };
    board.translations.forEach((tr) => {
      const lang = String(tr?.language || "").toLowerCase();
      if (!LANGS.includes(lang)) return;
      next[lang] = {
        title: tr?.title || "",
        heroDescription: tr?.heroDescription || tr?.description || "",
        recommendationsIntro: tr?.recommendationsIntro || "",
      };
    });
    return next;
  })(),
  isActive:
    typeof board?.isActive === "boolean"
      ? board.isActive
      : typeof board?.active === "boolean"
      ? board.active
      : true,
  keyFeatures: Array.isArray(board?.keyFeatures)
    ? board.keyFeatures.map((f) => (typeof f === "string" ? f : f?.text || f?.title || "")).filter(Boolean)
    : [""],
  specificationSections: Array.isArray(board?.specificationSections)
    ? board.specificationSections.map((s, sectionIdx) => ({
        heading: s?.heading || s?.title || s?.name || "",
        displayOrder: Number(s?.displayOrder) || sectionIdx + 1,
        items: Array.isArray(s?.items)
          ? s.items.map((it, itemIdx) => ({
              label: it?.label || "",
              value: it?.value || "",
              displayOrder: Number(it?.displayOrder) || itemIdx + 1,
            }))
          : [{ label: "", value: "", displayOrder: 1 }],
      }))
    : [{ heading: "", displayOrder: 1, items: [{ label: "", value: "", displayOrder: 1 }] }],
  recommendationApplications: Array.isArray(board?.recommendationApplications)
    ? board.recommendationApplications.map((r, idx) =>
        typeof r === "string"
          ? { numberLabel: String(idx + 1).padStart(2, "0"), text: r }
          : {
              numberLabel: r?.numberLabel || String(idx + 1).padStart(2, "0"),
              text: r?.text || r?.body || "",
            }
      )
    : [{ numberLabel: "01", text: "" }],
  recommendationAccordionItems: Array.isArray(board?.recommendationAccordionItems)
    ? board.recommendationAccordionItems.map((a, idx) => ({
        title: a?.title || "",
        displayOrder: Number(a?.displayOrder) || idx + 1,
        bullets: Array.isArray(a?.bullets)
          ? a.bullets.map((b, bIdx) => ({
              text: b?.text || "",
              displayOrder: Number(b?.displayOrder) || bIdx + 1,
            }))
          : [{ text: a?.body || a?.text || "", displayOrder: 1 }],
      }))
    : [{ title: "", displayOrder: 1, bullets: [{ text: "", displayOrder: 1 }] }],
  sustainabilityCards: Array.isArray(board?.sustainabilityCards)
    ? board.sustainabilityCards.map((c) => ({
        title: c?.title || "",
        text: c?.text || c?.body || "",
        iconUrl: c?.iconUrl || c?.icon || "",
      }))
    : [{ title: "", text: "", iconUrl: "" }],
  sustainabilityStorageItems: Array.isArray(board?.sustainabilityStorageItems)
    ? board.sustainabilityStorageItems.map((i) =>
        typeof i === "string" ? { text: i } : { text: i?.text || i?.body || "" }
      )
    : [{ text: "" }],
});

const toPayload = (form) => {
  const language = String(form.language || "en").toLowerCase();
  const activeTranslation = form.translations?.[language] || form.translations?.en || emptyLangFields();
  return {
  name: activeTranslation.title.trim(),
  boardType: form.boardType.trim(),
  language,
  heroDescription: activeTranslation.heroDescription.trim(),
  imageUrl: form.imageUrl?.trim() || null,
  recommendationsIntro: activeTranslation.recommendationsIntro.trim(),
  isActive: Boolean(form.isActive),
  keyFeatures: (form.keyFeatures || []).filter(Boolean).map((text) => ({ text })),
  specificationSections: (form.specificationSections || [])
    .filter((s) => s.heading || (s.items || []).some((it) => it.label || it.value))
    .map((s) => ({
      heading: s.heading || "",
      items: (s.items || [])
        .filter((it) => it.label || it.value)
        .map((it) => ({
          label: it.label || "",
          value: it.value || "",
        })),
    })),
  recommendationApplications: (form.recommendationApplications || [])
    .map((app, idx) =>
      typeof app === "string"
        ? { numberLabel: String(idx + 1).padStart(2, "0"), text: app }
        : { numberLabel: app?.numberLabel || String(idx + 1).padStart(2, "0"), text: app?.text || "" }
    )
    .filter((app) => app.text),
  recommendationAccordionItems: (form.recommendationAccordionItems || [])
    .filter((a) => a.title || (a.bullets || []).some((b) => b.text))
    .map((a) => ({
      title: a.title || "",
      bullets: (a.bullets || [])
        .filter((b) => b.text)
        .map((b) => ({ text: b.text || "" })),
    })),
  sustainabilityCards: (form.sustainabilityCards || [])
    .filter((c) => c.title || c.text || c.iconUrl)
    .map((c) => ({ title: c.title, text: c.text, iconUrl: c.iconUrl })),
  sustainabilityStorageItems: (form.sustainabilityStorageItems || [])
    .map((item) => (typeof item === "string" ? { text: item } : { text: item?.text || "" }))
    .filter((item) => item.text),
  };
};

const BoardsManager = () => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [createImageFile, setCreateImageFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState({});

  const loadBoards = async () => {
    setLoading(true);
    try {
      const data = await getAdminBoards();
      setBoards(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load boards",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBoards();
  }, []);

  const refreshBoardRowById = async (id) => {
    if (id == null) {
      await loadBoards();
      return;
    }
    try {
      const one = await getAdminBoardById(id);
      setBoards((prev) => {
        const idx = prev.findIndex((x) => Number(x.id) === Number(id));
        if (idx === -1) return one ? [...prev, one] : prev;
        const next = [...prev];
        next[idx] = one;
        return next;
      });
    } catch {
      await loadBoards();
    }
  };

  const sortedBoards = useMemo(() => {
    const currentLang = String(i18n.language || "en")
      .toLowerCase()
      .split("-")[0];
    const filtered = boards.filter((board) => {
      const boardLang = String(board?.language || "en")
        .toLowerCase()
        .split("-")[0];
      return boardLang === currentLang;
    });
    return [...filtered].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [boards, i18n.language]);

  const handleCreate = async () => {
    const createLang = String(createForm.language || activeCreateLang || "en").toLowerCase();
    const createTitle = createForm.translations?.[createLang]?.title || "";
    if (!createTitle.trim() || !createForm.boardType.trim()) {
      notify({ type: "error", title: "Title and Board Type are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = toPayload({ ...createForm, language: createLang });
      const createdBoard = await createAdminBoard(payload);
      const createdId = createdBoard?.id;

      if (createImageFile) {
        if (createdId) {
          await uploadAdminBoardImage({ id: createdId, file: createImageFile });
        } else {
          notify({
            type: "error",
            title: "Image upload skipped",
            message: "Board created but image could not be uploaded automatically (missing board id in response).",
          });
        }
      }

      notify({ type: "success", title: "Board created" });
      setCreateForm(EMPTY_FORM);
      setCreateImageFile(null);
      setActiveCreateLang("en");
      await loadBoards();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = async (board) => {
    setEditingId(board.id);
    const mapped = mapBoardToForm(board);
    setEditForm(mapped);
    setActiveEditLang(mapped.language || "en");
    try {
      const fresh = await getAdminBoardById(board.id);
      setBoards((prev) => prev.map((x) => (Number(x.id) === Number(board.id) ? fresh : x)));
      const mappedFresh = mapBoardToForm(fresh);
      setEditForm(mappedFresh);
      setActiveEditLang(mappedFresh.language || "en");
    } catch {
      /* keep list row */
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const editLang = String(editForm.language || activeEditLang || "en").toLowerCase();
    const editTitle = editForm.translations?.[editLang]?.title || "";
    if (!editTitle.trim() || !editForm.boardType.trim()) {
      notify({ type: "error", title: "Title and Board Type are required" });
      return;
    }
    try {
      const payload = toPayload({ ...editForm, language: editLang });
      await updateAdminBoard({ id: editingId, payload });
      notify({ type: "success", title: "Board updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      await loadBoards();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleDelete = async (board) => {
    const ok = window.confirm(`Delete board "${getBoardName(board)}"?`);
    if (!ok) return;
    try {
      await deleteAdminBoard({ id: board.id });
      notify({ type: "success", title: "Board deleted" });
      await loadBoards();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Boards Manager</h2>
            <p className="text-sm text-slate-500 mt-1">CRUD boards and manage board images.</p>
          </div>
          <button
            type="button"
            onClick={loadBoards}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex items-center gap-2">
            {LANGS.map((lang) => (
              <button
                key={`create-${lang}`}
                type="button"
                onClick={() => {
                  setActiveCreateLang(lang);
                  setCreateForm((prev) => ({ ...prev, language: lang }));
                }}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  activeCreateLang === lang
                    ? "bg-[#011337] text-white border-[#011337]"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <label className="text-sm text-slate-700">
            Title ({activeCreateLang.toUpperCase()})
            <input
              type="text"
              value={createForm.translations[activeCreateLang].title}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  translations: {
                    ...prev.translations,
                    [activeCreateLang]: {
                      ...prev.translations[activeCreateLang],
                      title: e.target.value,
                    },
                  },
                }))
              }
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              placeholder="Regular Light Weight"
            />
          </label>
          <label className="text-sm text-slate-700">
            Board Type
            <input
              type="text"
              value={createForm.boardType}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, boardType: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              placeholder="regular / moisture / fire..."
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Hero Description ({activeCreateLang.toUpperCase()})
            <textarea
              value={createForm.translations[activeCreateLang].heroDescription}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  translations: {
                    ...prev.translations,
                    [activeCreateLang]: {
                      ...prev.translations[activeCreateLang],
                      heroDescription: e.target.value,
                    },
                  },
                }))
              }
              className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              placeholder="Board hero description"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Recommendations Intro ({activeCreateLang.toUpperCase()})
            <textarea
              value={createForm.translations[activeCreateLang].recommendationsIntro}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  translations: {
                    ...prev.translations,
                    [activeCreateLang]: {
                      ...prev.translations[activeCreateLang],
                      recommendationsIntro: e.target.value,
                    },
                  },
                }))
              }
              className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={Boolean(createForm.isActive)}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Board Image File
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCreateImageFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm"
            />
            <span className="mt-1 block text-xs text-slate-500">
              Optional. If selected, it uploads automatically after board creation.
            </span>
          </label>
        </div>
        <div className="mt-4 grid gap-4">
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Key Features</p>
            {createForm.keyFeatures.map((item, idx) => (
              <div key={`kf-${idx}`} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) =>
                    setCreateForm((prev) => {
                      const next = [...prev.keyFeatures];
                      next[idx] = e.target.value;
                      return { ...prev, keyFeatures: next };
                    })
                  }
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button type="button" onClick={() => setCreateForm((prev) => ({ ...prev, keyFeatures: prev.keyFeatures.filter((_, i) => i !== idx) }))} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
              </div>
            ))}
            <button type="button" onClick={() => setCreateForm((prev) => ({ ...prev, keyFeatures: [...prev.keyFeatures, ""] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add feature</button>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Specifications</p>
            {createForm.specificationSections.map((item, idx) => (
              <div key={`sp-${idx}`} className="mb-3 rounded-lg border border-slate-200 p-2">
                <div className="grid sm:grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={item.heading}
                    onChange={(e) =>
                      setCreateForm((prev) => {
                        const next = [...prev.specificationSections];
                        next[idx] = { ...next[idx], heading: e.target.value };
                        return { ...prev, specificationSections: next };
                      })
                    }
                    placeholder="Section heading"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    value={item.displayOrder}
                    onChange={(e) =>
                      setCreateForm((prev) => {
                        const next = [...prev.specificationSections];
                        next[idx] = { ...next[idx], displayOrder: Number(e.target.value) || idx + 1 };
                        return { ...prev, specificationSections: next };
                      })
                    }
                    placeholder="Display order"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                {(item.items || []).map((specItem, itemIdx) => (
                  <div key={`sp-item-${idx}-${itemIdx}`} className="grid sm:grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={specItem.label}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = [...prev.specificationSections];
                          const nextItems = [...(next[idx].items || [])];
                          nextItems[itemIdx] = { ...nextItems[itemIdx], label: e.target.value };
                          next[idx] = { ...next[idx], items: nextItems };
                          return { ...prev, specificationSections: next };
                        })
                      }
                      placeholder="Label"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      value={specItem.value}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = [...prev.specificationSections];
                          const nextItems = [...(next[idx].items || [])];
                          nextItems[itemIdx] = { ...nextItems[itemIdx], value: e.target.value };
                          next[idx] = { ...next[idx], items: nextItems };
                          return { ...prev, specificationSections: next };
                        })
                      }
                      placeholder="Value"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="number"
                      value={specItem.displayOrder}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = [...prev.specificationSections];
                          const nextItems = [...(next[idx].items || [])];
                          nextItems[itemIdx] = { ...nextItems[itemIdx], displayOrder: Number(e.target.value) || itemIdx + 1 };
                          next[idx] = { ...next[idx], items: nextItems };
                          return { ...prev, specificationSections: next };
                        })
                      }
                      placeholder="Order"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setCreateForm((prev) => {
                      const next = [...prev.specificationSections];
                      const items = [...(next[idx].items || []), { label: "", value: "", displayOrder: (next[idx].items?.length || 0) + 1 }];
                      next[idx] = { ...next[idx], items };
                      return { ...prev, specificationSections: next };
                    })
                  }
                  className="text-xs px-2 py-1 rounded border border-slate-300"
                >
                  Add spec item
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setCreateForm((prev) => ({
                  ...prev,
                  specificationSections: [
                    ...prev.specificationSections,
                    { heading: "", displayOrder: prev.specificationSections.length + 1, items: [{ label: "", value: "", displayOrder: 1 }] },
                  ],
                }))
              }
              className="text-xs px-3 py-1.5 rounded border border-slate-300"
            >
              Add specification section
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Recommendation Applications</p>
            {createForm.recommendationApplications.map((item, idx) => (
              <div key={`ra-${idx}`} className="grid sm:grid-cols-12 gap-2 mb-2">
                <input
                  type="text"
                  value={item?.numberLabel || ""}
                  onChange={(e) =>
                    setCreateForm((prev) => {
                      const next = [...prev.recommendationApplications];
                      next[idx] = { ...(next[idx] || {}), numberLabel: e.target.value };
                      return { ...prev, recommendationApplications: next };
                    })
                  }
                  placeholder="01"
                  className="sm:col-span-3 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  value={item?.text || ""}
                  onChange={(e) =>
                    setCreateForm((prev) => {
                      const next = [...prev.recommendationApplications];
                      next[idx] = { ...(next[idx] || {}), text: e.target.value };
                      return { ...prev, recommendationApplications: next };
                    })
                  }
                  placeholder="Application text"
                  className="sm:col-span-8 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button type="button" onClick={() => setCreateForm((prev) => ({ ...prev, recommendationApplications: prev.recommendationApplications.filter((_, i) => i !== idx) }))} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setCreateForm((prev) => ({
                  ...prev,
                  recommendationApplications: [
                    ...prev.recommendationApplications,
                    { numberLabel: String(prev.recommendationApplications.length + 1).padStart(2, "0"), text: "" },
                  ],
                }))
              }
              className="text-xs px-3 py-1.5 rounded border border-slate-300"
            >
              Add application
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Recommendation Accordion Items</p>
            {createForm.recommendationAccordionItems.map((item, idx) => (
              <div key={`rai-${idx}`} className="mb-3 rounded-lg border border-slate-200 p-2">
                <div className="grid sm:grid-cols-2 gap-2 mb-2">
                  <input type="text" value={item.title} onChange={(e) => setCreateForm((prev) => { const next=[...prev.recommendationAccordionItems]; next[idx]={...next[idx],title:e.target.value}; return {...prev,recommendationAccordionItems:next}; })} placeholder="Title" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                  <input type="number" value={item.displayOrder} onChange={(e) => setCreateForm((prev) => { const next=[...prev.recommendationAccordionItems]; next[idx]={...next[idx],displayOrder:Number(e.target.value)||idx+1}; return {...prev,recommendationAccordionItems:next}; })} placeholder="Order" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                </div>
                {(item.bullets || []).map((bullet, bIdx) => (
                  <div key={`rai-b-${idx}-${bIdx}`} className="grid sm:grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={bullet.text}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = [...prev.recommendationAccordionItems];
                          const bullets = [...(next[idx].bullets || [])];
                          bullets[bIdx] = { ...bullets[bIdx], text: e.target.value };
                          next[idx] = { ...next[idx], bullets };
                          return { ...prev, recommendationAccordionItems: next };
                        })
                      }
                      placeholder="Bullet text"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="number"
                      value={bullet.displayOrder}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = [...prev.recommendationAccordionItems];
                          const bullets = [...(next[idx].bullets || [])];
                          bullets[bIdx] = { ...bullets[bIdx], displayOrder: Number(e.target.value) || bIdx + 1 };
                          next[idx] = { ...next[idx], bullets };
                          return { ...prev, recommendationAccordionItems: next };
                        })
                      }
                      placeholder="Bullet order"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setCreateForm((prev) => {
                      const next = [...prev.recommendationAccordionItems];
                      next[idx] = {
                        ...next[idx],
                        bullets: [...(next[idx].bullets || []), { text: "", displayOrder: (next[idx].bullets?.length || 0) + 1 }],
                      };
                      return { ...prev, recommendationAccordionItems: next };
                    })
                  }
                  className="text-xs px-2 py-1 rounded border border-slate-300"
                >
                  Add bullet
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setCreateForm((prev) => ({ ...prev, recommendationAccordionItems: [...prev.recommendationAccordionItems, { title: "", displayOrder: prev.recommendationAccordionItems.length + 1, bullets: [{ text: "", displayOrder: 1 }] }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add accordion item</button>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Sustainability Cards</p>
            {createForm.sustainabilityCards.map((item, idx) => (
              <div key={`sc-${idx}`} className="grid sm:grid-cols-3 gap-2 mb-2">
                <input type="text" value={item.title} onChange={(e) => setCreateForm((prev) => { const next=[...prev.sustainabilityCards]; next[idx]={...next[idx],title:e.target.value}; return {...prev,sustainabilityCards:next}; })} placeholder="Title" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <input type="text" value={item.text} onChange={(e) => setCreateForm((prev) => { const next=[...prev.sustainabilityCards]; next[idx]={...next[idx],text:e.target.value}; return {...prev,sustainabilityCards:next}; })} placeholder="Text" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <input type="text" value={item.iconUrl} onChange={(e) => setCreateForm((prev) => { const next=[...prev.sustainabilityCards]; next[idx]={...next[idx],iconUrl:e.target.value}; return {...prev,sustainabilityCards:next}; })} placeholder="Icon URL" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
            ))}
            <button type="button" onClick={() => setCreateForm((prev) => ({ ...prev, sustainabilityCards: [...prev.sustainabilityCards, { title: "", text: "", iconUrl: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add sustainability card</button>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Sustainability Storage Items</p>
            {createForm.sustainabilityStorageItems.map((item, idx) => (
              <div key={`ssi-${idx}`} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={item?.text || ""}
                  onChange={(e) =>
                    setCreateForm((prev) => {
                      const next = [...prev.sustainabilityStorageItems];
                      next[idx] = { ...(next[idx] || {}), text: e.target.value };
                      return { ...prev, sustainabilityStorageItems: next };
                    })
                  }
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button type="button" onClick={() => setCreateForm((prev) => ({ ...prev, sustainabilityStorageItems: prev.sustainabilityStorageItems.filter((_, i) => i !== idx) }))} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setCreateForm((prev) => ({
                  ...prev,
                  sustainabilityStorageItems: [...prev.sustainabilityStorageItems, { text: "" }],
                }))
              }
              className="text-xs px-3 py-1.5 rounded border border-slate-300"
            >
              Add storage item
            </button>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create board"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-[8%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="w-[20%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="w-[22%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Image</th>
              <th className="w-[20%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Upload/Remove</th>
              <th className="w-[16%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading boards...</td>
              </tr>
            ) : sortedBoards.length ? (
              sortedBoards.map((board) => {
                const boardImage = resolveMediaUrl(board?.imageUrl);
                const isActive =
                  typeof board?.isActive === "boolean"
                    ? board.isActive
                    : typeof board?.active === "boolean"
                    ? board.active
                    : null;
                return (
                  <tr key={board.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{board.id}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{getBoardName(board)}</td>
                    <td className="px-3 py-3 text-sm">
                      {isActive == null ? (
                        <span className="text-slate-500">-</span>
                      ) : (
                        <span className={`px-2 py-1 rounded text-xs ${isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {boardImage ? (
                        <img src={boardImage} alt="" className="w-28 h-20 object-cover rounded border border-slate-200" />
                      ) : (
                        <p className="text-xs text-slate-500">No image</p>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <input
                          type="file"
                          onChange={(e) =>
                            setUploadFiles((prev) => ({ ...prev, [board.id]: e.target.files?.[0] || null }))
                          }
                          className="text-xs"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={!uploadFiles[board.id]}
                            onClick={async () => {
                              try {
                                await uploadAdminBoardImage({ id: board.id, file: uploadFiles[board.id] });
                                notify({ type: "success", title: "Image uploaded" });
                                setUploadFiles((prev) => ({ ...prev, [board.id]: null }));
                                await loadBoards();
                              } catch (error) {
                                notify({
                                  type: "error",
                                  title: "Upload failed",
                                  message: error?.response?.data?.message || error?.message || "",
                                });
                              }
                            }}
                            className="text-xs px-2 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                          >
                            Upload
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await removeAdminBoardImage({ id: board.id });
                                notify({ type: "success", title: "Image removed" });
                                await loadBoards();
                              } catch (error) {
                                notify({
                                  type: "error",
                                  title: "Remove failed",
                                  message: error?.response?.data?.message || error?.message || "",
                                });
                              }
                            }}
                            className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(board)}
                          className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(board)}
                          className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">No boards found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Board #{editingId}</h3>
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setEditForm(EMPTY_FORM);
                setActiveEditLang("en");
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 flex items-center gap-2">
              {LANGS.map((lang) => (
                <button
                  key={`edit-${lang}`}
                  type="button"
                  onClick={() => {
                    setActiveEditLang(lang);
                    setEditForm((prev) => ({ ...prev, language: lang }));
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    activeEditLang === lang
                      ? "bg-[#011337] text-white border-[#011337]"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <label className="text-sm text-slate-700">
              Title ({activeEditLang.toUpperCase()})
              <input
                type="text"
                value={editForm.translations[activeEditLang].title}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [activeEditLang]: {
                        ...prev.translations[activeEditLang],
                        title: e.target.value,
                      },
                    },
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-sm text-slate-700">
              Board Type
              <input
                type="text"
                value={editForm.boardType}
                onChange={(e) => setEditForm((prev) => ({ ...prev, boardType: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">
              Hero Description ({activeEditLang.toUpperCase()})
              <textarea
                value={editForm.translations[activeEditLang].heroDescription}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [activeEditLang]: {
                        ...prev.translations[activeEditLang],
                        heroDescription: e.target.value,
                      },
                    },
                  }))
                }
                className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">
              Recommendations Intro ({activeEditLang.toUpperCase()})
              <textarea
                value={editForm.translations[activeEditLang].recommendationsIntro}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [activeEditLang]: {
                        ...prev.translations[activeEditLang],
                        recommendationsIntro: e.target.value,
                      },
                    },
                  }))
                }
                className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(editForm.isActive)}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Key Features</p>
            {editForm.keyFeatures.map((item, idx) => (
              <div key={`ekf-${idx}`} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) =>
                    setEditForm((prev) => {
                      const next = [...prev.keyFeatures];
                      next[idx] = e.target.value;
                      return { ...prev, keyFeatures: next };
                    })
                  }
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button type="button" onClick={() => setEditForm((prev) => ({ ...prev, keyFeatures: prev.keyFeatures.filter((_, i) => i !== idx) }))} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
              </div>
            ))}
            <button type="button" onClick={() => setEditForm((prev) => ({ ...prev, keyFeatures: [...prev.keyFeatures, ""] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add feature</button>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Specifications</p>
            {editForm.specificationSections.map((item, idx) => (
              <div key={`esp-${idx}`} className="mb-3 rounded-lg border border-slate-200 p-2">
                <div className="grid sm:grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={item.heading}
                    onChange={(e) =>
                      setEditForm((prev) => {
                        const next = [...prev.specificationSections];
                        next[idx] = { ...next[idx], heading: e.target.value };
                        return { ...prev, specificationSections: next };
                      })
                    }
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    value={item.displayOrder}
                    onChange={(e) =>
                      setEditForm((prev) => {
                        const next = [...prev.specificationSections];
                        next[idx] = { ...next[idx], displayOrder: Number(e.target.value) || idx + 1 };
                        return { ...prev, specificationSections: next };
                      })
                    }
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                {(item.items || []).map((specItem, itemIdx) => (
                  <div key={`esp-item-${idx}-${itemIdx}`} className="grid sm:grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={specItem.label}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = [...prev.specificationSections];
                          const nextItems = [...(next[idx].items || [])];
                          nextItems[itemIdx] = { ...nextItems[itemIdx], label: e.target.value };
                          next[idx] = { ...next[idx], items: nextItems };
                          return { ...prev, specificationSections: next };
                        })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      value={specItem.value}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = [...prev.specificationSections];
                          const nextItems = [...(next[idx].items || [])];
                          nextItems[itemIdx] = { ...nextItems[itemIdx], value: e.target.value };
                          next[idx] = { ...next[idx], items: nextItems };
                          return { ...prev, specificationSections: next };
                        })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="number"
                      value={specItem.displayOrder}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = [...prev.specificationSections];
                          const nextItems = [...(next[idx].items || [])];
                          nextItems[itemIdx] = { ...nextItems[itemIdx], displayOrder: Number(e.target.value) || itemIdx + 1 };
                          next[idx] = { ...next[idx], items: nextItems };
                          return { ...prev, specificationSections: next };
                        })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                ))}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setEditForm((prev) => ({
                  ...prev,
                  specificationSections: [
                    ...prev.specificationSections,
                    { heading: "", displayOrder: prev.specificationSections.length + 1, items: [{ label: "", value: "", displayOrder: 1 }] },
                  ],
                }))
              }
              className="text-xs px-3 py-1.5 rounded border border-slate-300"
            >
              Add specification section
            </button>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Recommendation Applications</p>
            {editForm.recommendationApplications.map((item, idx) => (
              <div key={`era-${idx}`} className="grid sm:grid-cols-12 gap-2 mb-2">
                <input
                  type="text"
                  value={item?.numberLabel || ""}
                  onChange={(e) =>
                    setEditForm((prev) => {
                      const next = [...prev.recommendationApplications];
                      next[idx] = { ...(next[idx] || {}), numberLabel: e.target.value };
                      return { ...prev, recommendationApplications: next };
                    })
                  }
                  className="sm:col-span-3 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  value={item?.text || ""}
                  onChange={(e) =>
                    setEditForm((prev) => {
                      const next = [...prev.recommendationApplications];
                      next[idx] = { ...(next[idx] || {}), text: e.target.value };
                      return { ...prev, recommendationApplications: next };
                    })
                  }
                  className="sm:col-span-9 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setEditForm((prev) => ({
                  ...prev,
                  recommendationApplications: [
                    ...prev.recommendationApplications,
                    { numberLabel: String(prev.recommendationApplications.length + 1).padStart(2, "0"), text: "" },
                  ],
                }))
              }
              className="text-xs px-3 py-1.5 rounded border border-slate-300"
            >
              Add application
            </button>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 p-3">
            <p className="text-sm font-semibold text-[#011337] mb-2">Recommendation Accordion Items</p>
            {editForm.recommendationAccordionItems.map((item, idx) => (
              <div key={`erai-${idx}`} className="mb-3 rounded-lg border border-slate-200 p-2">
                <div className="grid sm:grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) =>
                      setEditForm((prev) => {
                        const next = [...prev.recommendationAccordionItems];
                        next[idx] = { ...next[idx], title: e.target.value };
                        return { ...prev, recommendationAccordionItems: next };
                      })
                    }
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    value={item.displayOrder}
                    onChange={(e) =>
                      setEditForm((prev) => {
                        const next = [...prev.recommendationAccordionItems];
                        next[idx] = { ...next[idx], displayOrder: Number(e.target.value) || idx + 1 };
                        return { ...prev, recommendationAccordionItems: next };
                      })
                    }
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                {(item.bullets || []).map((bullet, bIdx) => (
                  <div key={`erai-b-${idx}-${bIdx}`} className="grid sm:grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={bullet.text}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = [...prev.recommendationAccordionItems];
                          const bullets = [...(next[idx].bullets || [])];
                          bullets[bIdx] = { ...bullets[bIdx], text: e.target.value };
                          next[idx] = { ...next[idx], bullets };
                          return { ...prev, recommendationAccordionItems: next };
                        })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="number"
                      value={bullet.displayOrder}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = [...prev.recommendationAccordionItems];
                          const bullets = [...(next[idx].bullets || [])];
                          bullets[bIdx] = { ...bullets[bIdx], displayOrder: Number(e.target.value) || bIdx + 1 };
                          next[idx] = { ...next[idx], bullets };
                          return { ...prev, recommendationAccordionItems: next };
                        })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setEditForm((prev) => {
                      const next = [...prev.recommendationAccordionItems];
                      next[idx] = {
                        ...next[idx],
                        bullets: [...(next[idx].bullets || []), { text: "", displayOrder: (next[idx].bullets?.length || 0) + 1 }],
                      };
                      return { ...prev, recommendationAccordionItems: next };
                    })
                  }
                  className="text-xs px-2 py-1 rounded border border-slate-300"
                >
                  Add bullet
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setEditForm((prev) => ({
                  ...prev,
                  recommendationAccordionItems: [
                    ...prev.recommendationAccordionItems,
                    { title: "", displayOrder: prev.recommendationAccordionItems.length + 1, bullets: [{ text: "", displayOrder: 1 }] },
                  ],
                }))
              }
              className="text-xs px-3 py-1.5 rounded border border-slate-300"
            >
              Add accordion item
            </button>
          </div>
          <AdminProductPdfsPanel
            variant="board"
            productId={editingId}
            productSlug={String(boards.find((x) => x.id === editingId)?.boardType ?? "").trim()}
            pdfDocuments={boards.find((x) => x.id === editingId)?.pdfDocuments}
            onRefresh={() => refreshBoardRowById(editingId)}
          />
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={saveEdit}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm"
            >
              Save changes
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default BoardsManager;
